const COLLECTION_MENU_INTENT_KEY = 'prior-era:open-collection-mega-menu';

function normalizeMenuPath(pathname) {
  return pathname.replace(/\/+$/, '') || '/';
}

class PriorEraCollectionMegaMenu extends HTMLElement {
  connectedCallback() {
    this.anchor = this.querySelector('[data-collection-mega-menu-link]');
    this.details = this.querySelector('details');
    this.trigger = this.querySelector('summary');
    if (!this.anchor || !this.details || !this.trigger) return;

    this.handleToggle = this.handleToggle.bind(this);
    this.handleDocumentClick = this.handleDocumentClick.bind(this);
    this.handleDocumentKeydown = this.handleDocumentKeydown.bind(this);
    this.handleFocusOut = this.handleFocusOut.bind(this);
    this.handleParentClick = this.handleParentClick.bind(this);
    this.handlePointerEnter = this.handlePointerEnter.bind(this);
    this.handlePointerLeave = this.handlePointerLeave.bind(this);

    this.details.addEventListener('toggle', this.handleToggle);
    this.anchor.addEventListener('click', this.handleParentClick);
    this.addEventListener('focusout', this.handleFocusOut);
    this.addEventListener('pointerenter', this.handlePointerEnter);
    this.addEventListener('pointerleave', this.handlePointerLeave);
    document.addEventListener('click', this.handleDocumentClick);
    document.addEventListener('keydown', this.handleDocumentKeydown);
    this.syncExpanded();
  }

  disconnectedCallback() {
    if (!this.anchor || !this.details || !this.trigger) return;

    this.details.removeEventListener('toggle', this.handleToggle);
    this.anchor.removeEventListener('click', this.handleParentClick);
    this.removeEventListener('focusout', this.handleFocusOut);
    this.removeEventListener('pointerenter', this.handlePointerEnter);
    this.removeEventListener('pointerleave', this.handlePointerLeave);
    document.removeEventListener('click', this.handleDocumentClick);
    document.removeEventListener('keydown', this.handleDocumentKeydown);
  }

  handleParentClick(event) {
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey ||
      this.anchor.target === '_blank' ||
      window.matchMedia('(max-width: 989px)').matches
    ) {
      return;
    }

    const destination = new URL(this.anchor.href, window.location.href);
    const destinationPath = normalizeMenuPath(destination.pathname);
    const currentPath = normalizeMenuPath(window.location.pathname);

    if (destinationPath === currentPath) {
      event.preventDefault();
      this.details.open ? this.closeMenu() : this.openMenu();
      return;
    }

    try {
      sessionStorage.setItem(
        COLLECTION_MENU_INTENT_KEY,
        JSON.stringify({
          handle: this.dataset.menuHandle,
          path: destinationPath,
        })
      );
    } catch (error) {
      // Normal Shopify navigation still works if session storage is unavailable.
    }
  }

  handleToggle() {
    this.syncExpanded();
    if (!this.details.open) return;

    document.querySelectorAll('header-menu details[open]').forEach((otherDetails) => {
      if (otherDetails === this.details) return;

      otherDetails.removeAttribute('open');
      otherDetails.querySelector('summary')?.setAttribute('aria-expanded', 'false');
    });
  }

  handleDocumentClick(event) {
    if (this.details.open && !this.contains(event.target)) this.closeMenu();
  }

  handleDocumentKeydown(event) {
    if (event.key !== 'Escape' || !this.details.open) return;

    event.preventDefault();
    this.closeMenu();
    this.trigger.focus();
  }

  handleFocusOut() {
    window.requestAnimationFrame(() => {
      if (this.details.open && !this.contains(document.activeElement)) this.closeMenu();
    });
  }

  handlePointerEnter(event) {
    if (event.pointerType === 'touch') return;
    this.openMenu();
  }

  handlePointerLeave(event) {
    if (event.pointerType === 'touch' || this.contains(document.activeElement)) return;
    this.closeMenu();
  }

  openMenu(focusTrigger = false) {
    this.details.open = true;
    this.syncExpanded();
    if (focusTrigger) this.trigger.focus({ preventScroll: true });
  }

  closeMenu() {
    this.details.open = false;
    this.syncExpanded();
  }

  syncExpanded() {
    this.trigger.setAttribute('aria-expanded', this.details.open ? 'true' : 'false');
  }
}

if (!customElements.get('prior-era-collection-mega-menu')) {
  customElements.define('prior-era-collection-mega-menu', PriorEraCollectionMegaMenu);
}

function consumeCollectionMenuIntent() {
  let pendingIntent;

  try {
    const storedIntent = sessionStorage.getItem(COLLECTION_MENU_INTENT_KEY);
    if (!storedIntent) return;

    sessionStorage.removeItem(COLLECTION_MENU_INTENT_KEY);
    pendingIntent = JSON.parse(storedIntent);
  } catch (error) {
    return;
  }

  if (!pendingIntent?.handle || pendingIntent.path !== normalizeMenuPath(window.location.pathname)) return;

  const targetMenu = [...document.querySelectorAll('prior-era-collection-mega-menu')].find(
    (menu) => menu.dataset.menuHandle === pendingIntent.handle
  );
  if (!targetMenu || !targetMenu.details || !targetMenu.anchor) return;

  const menuPath = normalizeMenuPath(new URL(targetMenu.anchor.href, window.location.href).pathname);
  if (menuPath === pendingIntent.path) targetMenu.openMenu(true);
}

document.addEventListener('click', (event) => {
  const toggle = event.target.closest('button.pe-menu-drawer__parent-toggle');
  if (!toggle || !window.matchMedia('(max-width: 989px)').matches) return;

  event.stopImmediatePropagation();

  const panel = document.getElementById(toggle.getAttribute('aria-controls'));
  if (!panel) return;

  const isExpanded = toggle.getAttribute('aria-expanded') === 'true';
  toggle.setAttribute('aria-expanded', isExpanded ? 'false' : 'true');
  panel.hidden = isExpanded;
}, true);

let collectionSubmenuEscapeHandled = false;

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape' || !window.matchMedia('(max-width: 989px)').matches) return;

  const expandedToggles = [...document.querySelectorAll('button.pe-menu-drawer__parent-toggle[aria-expanded="true"]')];
  const toggle = expandedToggles.find((button) => button.closest('li')?.contains(document.activeElement));
  if (!toggle) return;

  const panel = document.getElementById(toggle.getAttribute('aria-controls'));
  if (!panel) return;

  event.preventDefault();
  event.stopPropagation();
  collectionSubmenuEscapeHandled = true;
  toggle.setAttribute('aria-expanded', 'false');
  panel.hidden = true;
  toggle.focus({ preventScroll: true });
});

document.addEventListener('keyup', (event) => {
  if (event.key !== 'Escape' || !collectionSubmenuEscapeHandled) return;

  event.preventDefault();
  event.stopImmediatePropagation();
  collectionSubmenuEscapeHandled = false;
}, true);

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', consumeCollectionMenuIntent, { once: true });
} else {
  consumeCollectionMenuIntent();
}
